/**
 * 长整型 ID 工具集
 *
 * 背景：
 *   1. 后端主键采用 Snowflake 算法，生成 19 位 Long（如 2085304488185540609）
 *   2. JS Number 安全整数上限 = 2^53-1 = 9007199254740991（16 位）
 *   3. JSON.parse 默认把数字字面量解析为 Number，超过安全范围会丢失精度
 *      2085304488185540609  →  2085304488185540600（末位归零）
 *
 * 解法：
 *   A. 解析层（唯一可靠层）：在 Axios transformResponse 阶段，于 JSON.parse 之前
 *      把响应原文里"会丢精度的整数字面量"改写为带引号字符串。见 quoteUnsafeIntegers。
 *      注意：必须在 parse 之前处理。一旦 parse 完成精度已经丢失，事后转换只会把
 *      错误值（...258800）包装成看似正确的字符串，属于掩盖问题，禁止这样做。
 *   B. 工具层（业务侧统一）：toSafeId / asString / asNumber / equalsId / normalizeIds，
 *      业务代码统一用此工具，不再写 Number(row.id) 这种散弹式代码。
 *
 * 收敛后规约：
 *   - 任何超过 Number.MAX_SAFE_INTEGER 的整数，前端一律视为 String
 *   - 后端 @RequestBody / @PathVariable 接收 String 类型的 ID，Spring/Jackson 会自动转 Long
 *   - 业务层做精确比较时，统一用 equalsId / asString 归一化
 *   - NUMERIC_KEEP_KEYS 是显式例外清单：这些字段业务上按数值使用，即使超范围也保持 Number
 */

export const NUMERIC_KEEP_KEYS = new Set([
  'total', 'count', 'size', 'length', 'width', 'height', 'weight',
  'amount', 'price', 'quantity', 'num', 'index', 'sort', 'orderNum',
  'pageNum', 'pageSize', 'level', 'progress', 'percent', 'score',
  'rate', 'ratio', 'duration', 'timeout', 'interval', 'threshold',
  'temperature', 'humidity', 'speed', 'voltage', 'current', 'power',
  'minValue', 'maxValue', 'precision', 'scale',
  'year', 'month', 'day', 'hour', 'minute', 'second'
])

export function isSafeInteger(value) {
  if (typeof value !== 'number') return false
  return Number.isFinite(value) && Math.abs(value) <= Number.MAX_SAFE_INTEGER
}

export function isKeepNumericKey(key) {
  if (!key) return false
  return NUMERIC_KEEP_KEYS.has(key) || /(Count|Total|Size|Num|Amount|Price)$/.test(key)
}

export function toSafeId(value) {
  if (value === null || value === undefined || value === '') return value
  if (typeof value === 'string') return value
  if (typeof value === 'bigint') return value.toString()
  if (typeof value === 'number') return isSafeInteger(value) ? value : String(value)
  return String(value)
}

export function asString(value) {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string') return value
  if (typeof value === 'bigint') return value.toString()
  return String(value)
}

export function asNumber(value) {
  if (value === null || value === undefined || value === '') return value
  if (typeof value === 'number') return value
  if (typeof value === 'bigint') return Number(value)
  const str = String(value).trim()
  if (!str) return value
  const num = Number(str)
  return Number.isFinite(num) ? num : value
}

export function equalsId(a, b) {
  if (a === b) return true
  if (a == null || b == null) return false
  return asString(a) === asString(b)
}

export function normalizeIds(ids) {
  if (!Array.isArray(ids)) return []
  return ids.map(asString)
}

function isDigit(ch) {
  return ch >= '0' && ch <= '9'
}

/**
 * 把 JSON 原文中"会丢失精度的整数字面量"改写为带引号字符串。
 *
 * 采用字符扫描而非正则：正则无法区分「JSON 结构中的数值」与「字符串内容中的数字」，
 * 例如 {"ids":"1,2085304488185540609"} 中字符串内部的逗号会被误命中，插入引号后
 * JSON 结构被破坏。扫描器显式跟踪引号内外状态、容器栈与当前 key，只改写真实数值位置。
 *
 * currentKey 仅在「key: 数值」这种直接绑定时生效：进入任何 { 或 [ 都会清空，
 * 避免 NUMERIC_KEEP_KEYS 白名单穿透到嵌套结构内部（如 {"total":[<19位ID>]}）。
 *
 * @param {string} text 原始响应文本
 * @returns {string} 改写后的文本；无需改写时返回原字符串引用
 */
export function quoteUnsafeIntegers(text) {
  if (typeof text !== 'string') return text
  if (text.length < 16) return text

  const len = text.length
  const stack = []
  let out = ''
  let copied = 0
  let i = 0
  let pendingString = null
  let currentKey = null

  while (i < len) {
    const ch = text[i]

    if (ch === '"') {
      const start = i
      i++
      while (i < len) {
        const c = text[i]
        if (c === '\\') {
          i += 2
          continue
        }
        i++
        if (c === '"') break
      }
      pendingString = text.slice(start + 1, i - 1)
      continue
    }

    if (ch === ':') {
      currentKey = pendingString
      pendingString = null
      i++
      continue
    }

    if (ch === ',') {
      pendingString = null
      currentKey = null
      i++
      continue
    }

    if (ch === '{' || ch === '[') {
      stack.push(currentKey)
      currentKey = null
      pendingString = null
      i++
      continue
    }

    if (ch === '}' || ch === ']') {
      currentKey = stack.length > 0 ? stack.pop() : null
      pendingString = null
      i++
      continue
    }

    if (isDigit(ch) || (ch === '-' && isDigit(text[i + 1]))) {
      const start = i
      if (ch === '-') i++
      while (i < len && isDigit(text[i])) i++
      const intEnd = i
      const next = text[i]
      const isPlainInteger = next !== '.' && next !== 'e' && next !== 'E'

      // 小数或指数：必须把整个数值词法单元一次性消费完，
      // 否则小数尾数（如 0.20833333333333334 的 20833333333333334）
      // 会在下一轮循环被当成独立的长整数误改写。
      if (!isPlainInteger) {
        if (text[i] === '.') {
          i++
          while (i < len && isDigit(text[i])) i++
        }
        if (text[i] === 'e' || text[i] === 'E') {
          i++
          if (text[i] === '+' || text[i] === '-') i++
          while (i < len && isDigit(text[i])) i++
        }
        continue
      }

      const raw = text.slice(start, intEnd)
      const digitCount = ch === '-' ? raw.length - 1 : raw.length
      if (digitCount >= 16 && !isSafeInteger(Number(raw)) && !isKeepNumericKey(currentKey)) {
        out += text.slice(copied, start) + '"' + raw + '"'
        copied = intEnd
      }
      continue
    }

    i++
  }

  return copied === 0 ? text : out + text.slice(copied)
}

/**
 * 等价于 JSON.parse，但不会丢失长整型精度。
 * 解析失败时与 JSON.parse 行为一致，直接抛出 SyntaxError，不做静默降级。
 */
export function safeJsonParse(text) {
  if (typeof text !== 'string') return text
  return JSON.parse(quoteUnsafeIntegers(text))
}

export default {
  NUMERIC_KEEP_KEYS,
  isSafeInteger,
  isKeepNumericKey,
  toSafeId,
  asString,
  asNumber,
  equalsId,
  normalizeIds,
  quoteUnsafeIntegers,
  safeJsonParse
}
